import type { Alpine } from 'alpinejs';

type MenuGroup = { label: string; details: HTMLDetailsElement | null; content: HTMLElement; button: HTMLButtonElement };
type Sidebar = {
    key: string; title: string; layout: HTMLElement; dialog: HTMLDialogElement; header: HTMLElement;
    nav: HTMLElement; rail: HTMLElement; collapse: HTMLButtonElement; expand: HTMLButtonElement;
    mobileButton: HTMLButtonElement; close: HTMLButtonElement; groups: MenuGroup[];
};
const menus = new WeakMap<HTMLElement, Sidebar>();
const collapsedKey = 'ab4h.sidebar.collapsed';
const cleanText = (node: Node) => node.textContent?.replace(/\s+/g, ' ').trim() || '';
const icons: Record<string, string> = {
    clubs: 'home', people: 'torsos-all', registrations: 'clipboard-notes', 'non-clubs': 'torsos', other: 'widget',
    programs: 'clipboard', moneris: 'credit-card', notifications: 'megaphone', leaders: 'torsos', volunteers: 'torsos-all',
    members: 'torsos-all', finance: 'dollar', medallions: 'trophy', overview: 'list', 'saved queries': 'puzzle',
};

function button(label: string, icon: string, className: string) {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = `dui-btn dui-btn-ghost ${className}`;
    el.setAttribute('aria-label', label);
    el.title = label;
    const symbol = document.createElement('i');
    symbol.className = `fi-${icon}`;
    symbol.setAttribute('aria-hidden', 'true');
    el.append(symbol);
    return el;
}

/** Keep the server-rendered controls and their Alpine scopes; only group existing nodes. */
export function enhanceSidebars(root: ParentNode) {
    const panels = [...(root instanceof HTMLElement && root.matches('[data-sidebar]') ? [root] : []), ...root.querySelectorAll<HTMLElement>('[data-sidebar]')];
    for (const panel of panels) {
        if (menus.has(panel)) continue;
        const sticky = panel.parentElement;
        const column = sticky?.closest<HTMLElement>('[data-sticky-container]');
        const layout = column?.parentElement;
        const main = column?.nextElementSibling;
        const heading = panel.querySelector<HTMLElement>(':scope > h3');
        if (!sticky || !column || !layout || !(main instanceof HTMLElement) || !heading) continue;
        const key = panel.dataset.sidebar!;
        const title = cleanText(heading);
        const originalNodes = [...panel.childNodes].filter(node => node !== heading);
        const sectionIcon = heading.querySelector('i')?.className.match(/fi-([\w-]+)/)?.[1] || 'list';
        const header = document.createElement('div');
        header.className = 'ab-sidebar-heading';
        heading.id ||= `sidebar-${key}-title`;
        const collapse = button(`Collapse ${title} menu`, 'indent-less', 'ab-sidebar-collapse');
        const close = button(`Close ${title} menu`, 'x', 'ab-sidebar-close');
        header.append(heading, collapse, close);
        const nav = document.createElement('nav');
        nav.className = 'ab-sidebar-nav';
        nav.id = `sidebar-${key}-navigation`;
        nav.setAttribute('aria-label', title);
        collapse.setAttribute('aria-controls', nav.id);
        const overview = document.createElement('div');
        overview.className = 'ab-sidebar-links ab-sidebar-overview';
        nav.append(overview);
        const list = document.createElement('ul');
        list.className = 'dui-menu ab-sidebar-groups';
        nav.append(list);
        const rail = document.createElement('div');
        rail.className = 'ab-sidebar-rail';
        const expand = button(`Expand ${title} menu`, 'indent-more', 'ab-sidebar-expand');
        expand.setAttribute('aria-controls', nav.id);
        rail.append(expand);
        const groups: MenuGroup[] = [];
        function addGroup(label: string, content: HTMLElement, details: HTMLDetailsElement | null, icon: string) {
            const control = button(`Open ${label}`, icon, 'ab-sidebar-rail-link');
            const text = document.createElement('span');
            text.textContent = label;
            control.append(text);
            content.id = `sidebar-${key}-group-${groups.length}`;
            control.setAttribute('aria-controls', content.id);
            rail.append(control);
            groups.push({ label, details, content, button: control });
        }
        addGroup('Overview', overview, null, sectionIcon);
        let destination = overview;
        for (const node of originalNodes) {
            if (node instanceof HTMLElement && node.matches('h3,h4,h5')) {
                const label = cleanText(node);
                const item = document.createElement('li');
                const details = document.createElement('details');
                details.open = true;
                const summary = document.createElement('summary');
                summary.append(node);
                const content = document.createElement('div');
                content.className = 'ab-sidebar-links';
                details.append(summary, content);
                item.append(details);
                list.append(item);
                addGroup(label, content, details, icons[label.toLowerCase()] || 'list');
                destination = content;
            } else destination.append(node);
        }
        // Empty introduction sections need no rail shortcut. Informational menus still have Expand.
        groups[0]!.button.hidden = !cleanText(overview) && !overview.querySelector('a,button');
        // Overview must also be collapsible so a rail shortcut can show just its chosen section.
        if (groups.length > 1 && !groups[0]!.button.hidden) {
            const item = document.createElement('li');
            const details = document.createElement('details');
            const summary = document.createElement('summary');
            const label = document.createElement('h4');
            label.textContent = 'Overview';
            summary.append(label);
            details.open = true;
            details.append(summary, overview);
            item.append(details);
            list.prepend(item);
            groups[0]!.details = details;
        }
        panel.replaceChildren(header, nav, rail);
        panel.classList.add('ab-sidebar');
        panel.setAttribute('x-ab-sidebar', '');
        if (!panel.hasAttribute('x-data')) panel.setAttribute('x-data', '{}');
        layout.classList.add('ab-sidebar-layout');
        column.classList.add('ab-sidebar-column');
        main.classList.add('ab-sidebar-main');
        sticky.classList.add('ab-sidebar-sticky');
        sticky.querySelectorAll(':scope > br').forEach(el => el.remove());
        main.querySelector(':scope > br')?.remove();
        const dialog = document.createElement('dialog');
        dialog.className = 'ab-sidebar-surface';
        dialog.setAttribute('aria-label', `${title} menu`);
        panel.before(dialog);
        dialog.append(panel);
        const mobileButton = button(`${title} menu`, 'list', 'ab-sidebar-mobile');
        const mobileLabel = document.createElement('span');
        mobileLabel.textContent = `${title} menu`;
        mobileButton.append(mobileLabel);
        dialog.id = `sidebar-${key}-surface`;
        mobileButton.setAttribute('aria-controls', dialog.id);
        mobileButton.setAttribute('aria-expanded', 'false');
        mobileButton.setAttribute('aria-haspopup', 'dialog');
        sticky.before(mobileButton);
        menus.set(panel, { key, title, layout, dialog, header, nav, rail, collapse, expand, mobileButton, close, groups });
    }
}

function readPreference(key: string): unknown {
    try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
}
function savePreference(key: string, value: unknown) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* Navigation also works when storage is blocked. */ }
}

/** Match default admin routes and reordered query parameters, ignoring view-only state. */
function destination(url: URL) {
    const path = url.pathname.replace(/\.php$/, '').replace(/\/$/, '') || '/';
    const params = new URLSearchParams(url.search);
    for (const name of ['save', 'tab', 'print', 'run']) params.delete(name);
    if (path === '/admin') {
        const module = params.get('m') || 'clubs';
        params.set('m', module);
        if (!params.has('p')) params.set('p', module);
    } else if (!params.has('p')) params.set('p', path.split('/').at(-1) || '');
    if (['clubs', 'programs', 'users'].includes(params.get('p') || '') && !params.has('scope')) params.set('scope', 'active');
    return { path, params };
}

export function registerSidebar(Alpine: Alpine) {
    Alpine.directive('ab-sidebar', (panel, _directive, { cleanup }) => {
        const menu = menus.get(panel);
        if (!menu) return;
        const { dialog, layout, nav, header, rail, groups, collapse, expand, mobileButton, close } = menu;
        const mobile = matchMedia('(max-width: 39.99875rem)');
        let collapsed = readPreference(collapsedKey) === true;
        const preferenceKey = `ab4h.sidebar.groups.${menu.key}`;
        const stored = readPreference(preferenceKey);
        const saved: Record<string, boolean> = {};
        if (stored && typeof stored === 'object') for (const [key, value] of Object.entries(stored)) if (typeof value === 'boolean') saved[key] = value;
        groups.forEach(group => { if (group.details && saved[group.label] !== undefined) group.details.open = saved[group.label]!; });
        function activeLink() {
            const current = new URL(location.href);
            const target = destination(current);
            let best: HTMLAnchorElement | null = null;
            let score = -1;
            for (const link of nav.querySelectorAll<HTMLAnchorElement>('a[href]')) {
                link.removeAttribute('aria-current');
                link.classList.remove('ab-sidebar-active');
                const href = link.getAttribute('href');
                if (!href || href.startsWith('#') || link.hasAttribute('download') || link.target === '_blank') continue;
                const url = new URL(link.href, document.baseURI);
                if (url.origin !== current.origin || (url.hash && url.hash !== current.hash)) continue;
                const candidate = destination(url);
                if (candidate.path !== target.path || [...candidate.params].some(([key, value]) => target.params.get(key) !== value)) continue;
                const rank = candidate.params.size + (url.hash ? 1 : 0);
                if (rank > score) { score = rank; best = link; }
            }
            if (best) {
                best.setAttribute('aria-current', 'page');
                best.classList.add('ab-sidebar-active');
                const details = best.closest('details');
                if (details) details.open = true;
            }
            groups.forEach(group => group.button.classList.toggle('ab-sidebar-active', !!group.content.querySelector('[aria-current="page"]')));
        }
        activeLink();
        function render() {
            const compact = collapsed && !mobile.matches;
            layout.classList.toggle('ab-sidebar-compact', compact);
            header.hidden = compact;
            nav.hidden = compact;
            rail.hidden = !compact;
            collapse.setAttribute('aria-expanded', String(!compact));
            expand.setAttribute('aria-expanded', String(!compact));
        }
        function viewport() {
            if (dialog.open) dialog.close();
            document.documentElement.classList.remove('ab-sidebar-modal-open');
            if (mobile.matches) {
                dialog.setAttribute('role', 'dialog');
                dialog.setAttribute('aria-modal', 'true');
            } else {
                dialog.setAttribute('role', 'complementary');
                dialog.removeAttribute('aria-modal');
                dialog.open = true;
            }
            mobileButton.setAttribute('aria-expanded', 'false');
            render();
        }
        function closeMobile() { if (mobile.matches && dialog.open) dialog.close(); }
        const handleClick = (event: MouseEvent) => {
            const control = event.target instanceof Element ? event.target.closest<HTMLElement>('a,button') : null;
            if (control === collapse || control === expand) {
                collapsed = control === collapse;
                savePreference(collapsedKey, collapsed);
                render();
                (collapsed ? expand : collapse).focus();
            } else if (control === close) closeMobile();
            else if (control && rail.contains(control)) {
                const group = groups.find(group => group.button === control);
                if (!group) return;
                collapsed = false;
                savePreference(collapsedKey, false);
                render();
                groups.forEach(candidate => {
                    if (candidate.details) candidate.details.open = candidate === group;
                });
                saveGroups();
                const focus = group.details?.querySelector('summary') || group.content.querySelector<HTMLElement>('a,button') || collapse;
                focus.focus({ preventScroll: true });
                focus.scrollIntoView({ block: 'nearest' });
            } else if (control && nav.contains(control)) closeMobile();
        };
        const openMobile = () => {
            if (!mobile.matches || dialog.open) return;
            dialog.showModal();
            mobileButton.setAttribute('aria-expanded', 'true');
            document.documentElement.classList.add('ab-sidebar-modal-open');
            close.focus();
        };
        const onClose = () => {
            // Safari does not always return focus after Escape. Do not steal it from an action's modal.
            if (mobile.matches && (dialog.contains(document.activeElement) || document.activeElement === document.body)) mobileButton.focus();
            mobileButton.setAttribute('aria-expanded', 'false');
            document.documentElement.classList.remove('ab-sidebar-modal-open');
        };
        const outside = (event: MouseEvent) => {
            if (event.target !== dialog || !mobile.matches) return;
            const box = dialog.getBoundingClientRect();
            if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeMobile();
        };
        const saveGroups = () => {
            groups.forEach(group => { if (group.details) saved[group.label] = group.details.open; });
            savePreference(preferenceKey, saved);
        };
        const toggles = groups.flatMap(group => {
            if (!group.details) return [];
            const details = group.details;
            const toggle = saveGroups;
            details.addEventListener('toggle', toggle);
            return [() => details.removeEventListener('toggle', toggle)];
        });
        viewport();
        panel.addEventListener('click', handleClick, true);
        mobileButton.addEventListener('click', openMobile);
        dialog.addEventListener('close', onClose);
        dialog.addEventListener('click', outside);
        mobile.addEventListener('change', viewport);
        // Details toggle events are queued; flush the current state even on immediate navigation.
        window.addEventListener('pagehide', saveGroups);
        window.addEventListener('popstate', activeLink);
        window.addEventListener('hashchange', activeLink);
        cleanup(() => {
            panel.removeEventListener('click', handleClick, true);
            mobileButton.removeEventListener('click', openMobile);
            dialog.removeEventListener('close', onClose);
            dialog.removeEventListener('click', outside);
            mobile.removeEventListener('change', viewport);
            window.removeEventListener('pagehide', saveGroups);
            window.removeEventListener('popstate', activeLink);
            window.removeEventListener('hashchange', activeLink);
            document.documentElement.classList.remove('ab-sidebar-modal-open');
            toggles.forEach(remove => remove());
        });
    });
}
